export const highlightRange = (function () {
    // Wrap each text node in a given DOM Range with a <mark> or other element.
    // Breaks start and/or end node if needed.
    // Returns a function that cleans up the created highlight (not a perfect undo: split text nodes are
    // not merged again).
    //
    // Parameters:
    // - range: a DOM Range object. Note that as highlighting modifies the DOM, the range may be
    //   unusable afterwards
    // - tagName: the element used to wrap text nodes. Defaults to 'mark'.
    // - attributes: an Object defining any attributes to be set on the wrapper elements.
    function highlightRange(range, tagName = 'mark', attributes = {}) { 
      if (range.collapsed) return;
    
      // First put all nodes in an array (splits start and end nodes if needed)
      const nodes = textNodesInRange(range);
    
      // Highlight each node
      const highlightElements = [];
      for (const nodeIdx in nodes) {
        const highlightElement = wrapNodeInHighlight(nodes[nodeIdx], tagName, attributes);
        highlightElements.push(highlightElement);
      }
    
      // Return a function that cleans up the highlightElements.
      function removeHighlights() {
        // Remove each of the created highlightElements.
        for (const highlightIdx in highlightElements) {
          removeHighlight(highlightElements[highlightIdx]);
        }
      }
      return removeHighlights;
    }
    
    // Return an array of the text nodes in the range. Split the start and end nodes if required.
    function textNodesInRange(range) {
      // If the start or end node is a text node and only partly in the range, split it.
      if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset > 0) {
        const endOffset = range.endOffset; // (this may get lost when the splitting the node)
        const createdNode = range.startContainer.splitText(range.startOffset);
        if (range.endContainer === range.startContainer) {
          // If the end was in the same container, it will now be in the newly created node.
          range.setEnd(createdNode, endOffset - range.startOffset);
        }
        range.setStart(createdNode, 0);
      }
      if (
        range.endContainer.nodeType === Node.TEXT_NODE
        && range.endOffset < range.endContainer.length
      ) {
        range.endContainer.splitText(range.endOffset);
      }
    
      // Collect the text nodes.
      const walker = range.startContainer.ownerDocument.createTreeWalker(
        range.commonAncestorContainer,
        NodeFilter.SHOW_TEXT,
        node => range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
      );
      walker.currentNode = range.startContainer;
    
      const nodes = [];
      if (walker.currentNode.nodeType === Node.TEXT_NODE)
        nodes.push(walker.currentNode);
      while (walker.nextNode() && range.comparePoint(walker.currentNode, 0) !== 1)
        nodes.push(walker.currentNode);
      return nodes;
    }
    
    // Replace [node] with <tagName ...attributes>[node]</tagName>
    function wrapNodeInHighlight(node, tagName, attributes) {
      const highlightElement = node.ownerDocument.createElement(tagName);
      Object.keys(attributes).forEach(key => {
        highlightElement.setAttribute(key, attributes[key]);
      });
      const tempRange = node.ownerDocument.createRange();
      tempRange.selectNode(node);
      tempRange.surroundContents(highlightElement);
      return highlightElement;
    }
    
    // Remove a highlight element created with wrapNodeInHighlight.
    function removeHighlight(highlightElement) {
      if (highlightElement.childNodes.length === 1) {
        highlightElement.parentNode.replaceChild(highlightElement.firstChild, highlightElement);
      } else {
        // If the highlight somehow contains multiple nodes now, move them all.
        while (highlightElement.firstChild) {
          highlightElement.parentNode.insertBefore(highlightElement.firstChild, highlightElement);
        }
        highlightElement.remove();
      }
    }
    
    return highlightRange;
})();